"use client"

import { useMemo, useState, type ReactNode } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "freezer-btu-calculator",
  "energy-cost-calculator",
  "appliance-energy-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Freezer Energy Cost Calculator",
  category: "Calculator" as const,
  mediumDescription:
    "Estimate the annual electricity cost of running your standalone freezer. Sized using DOE EnergyGuide kWh-per-cu-ft baselines, EIA US average residential electricity rate, and adjustments for unit age and installation location.",
  faqs: [
    { question: "How much does it cost to run a freezer per year?", answer: "A modern 15 cu ft upright freezer averages around 375 kWh per year — about $49 at the EIA US average residential rate of 13¢/kWh. Chest freezers run about 20% lower." },
    { question: "Are chest freezers really more efficient than uprights?", answer: "Yes. Chest freezers use roughly 20% less energy per cubic foot because cold air sinks and stays put when the lid opens, while opening an upright lets cold air spill out." },
    { question: "Does freezer age really matter for energy use?", answer: "Substantially. A pre-2001 freezer can use 50% more energy than a current ENERGY STAR model — that's the difference between $50/yr and $75–100/yr at the same capacity." },
    { question: "What temperature should I set my freezer to?", answer: "USDA recommends 0°F (-18°C). Setting it colder wastes energy without improving food safety; setting it warmer compromises long-term storage quality." },
    { question: "Why does a garage freezer cost more to run?", answer: "Hot summer ambient temperatures force the compressor to work harder. We add a 20% adjustment for garage installations — and you should always buy a garage-rated unit." },
    { question: "Does keeping the freezer full save energy?", answer: "Yes — a full freezer holds cold better than a half-empty one. Frozen food acts as thermal mass. ENERGY STAR recommends keeping freezers around 75% full." },
  ],
  relatedTools: ["freezer-btu-calculator", "energy-cost-calculator", "appliance-energy-calculator"],
}

export function FreezerEnergyCostCalculatorPageClient({ children }: { children?: ReactNode }) {
  const [freezerSize, setFreezerSize] = useState("15")
  const [freezerType, setFreezerType] = useState("upright")
  const [age, setAge] = useState("new")
  const [location, setLocation] = useState("indoor")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // `parseFloat` behind `if (!size)` admitted every negative — `!size` is
    // false for -15 — so a negative cabinet produced negative kWh and a negative
    // annual cost. The floor and ceiling are the real range of a freezer
    // cabinet, which is also what makes a near-zero size impossible rather than
    // merely positive. FRIDGE-384.
    const size = readQuantity(freezerSize, { min: 1, max: 100 })
    if (size === null) return null

    const kwhPerCuFt = freezerType === "upright" ? 25 : 20
    let kwhPerYear = size * kwhPerCuFt

    if (age === "5-10") kwhPerYear *= 1.15
    else if (age === "10-15") kwhPerYear *= 1.30
    else if (age === "15+") kwhPerYear *= 1.50

    if (location === "garage") kwhPerYear *= 1.20
    else if (location === "basement") kwhPerYear *= 0.95

    const electricityRate = 0.13
    const annualCost = kwhPerYear * electricityRate
    const monthlyCost = annualCost / 12
    const dailyCost = annualCost / 365
    const tenYearCost = annualCost * 10

    return {
      kwhPerYear: Math.round(kwhPerYear),
      annualCost,
      monthlyCost,
      dailyCost,
      tenYearCost,
    }
  }, [freezerSize, freezerType, age, location])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What does your"
      heroTitleItalic="freezer"
      heroTitlePost="actually cost to run?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "DOE EnergyGuide + EIA rate"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "25", label: "kWh / cu ft / yr", note: "Upright base" },
            { value: "20", label: "kWh / cu ft / yr", note: "Chest base" },
            { value: "$0.13", label: "US avg rate", note: "EIA 2024" },
            { value: "+50%", label: "15+ yr units", note: "vs new" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "freezer-energy-cost-calculator")}
      cta={{
        title: "Replace that energy hog with an upright that sips electricity?",
        body: "Browse upright freezers — sortable by capacity, with verified energy use info.",
        buttonLabel: "Browse upright freezers",
        buttonHref: "/collections/upright-freezers",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Four inputs.
                <br />
                One real bill.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                DOE EnergyGuide baseline kWh, adjusted for unit age and installation location.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label htmlFor="freezer-energy-size" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Freezer size
                  </label>
                  <div className="relative">
                    <input
                      id="freezer-energy-size"
                      type="number"
                      value={freezerSize}
                      onChange={(e) => setFreezerSize(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">cu ft</span>
                  </div>
                </div>
                <div>
                  <label htmlFor="freezer-energy-type" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Freezer type
                  </label>
                  <select
                    id="freezer-energy-type"
                    value={freezerType}
                    onChange={(e) => setFreezerType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="upright">Upright</option>
                    <option value="chest">Chest freezer (20% more efficient)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="freezer-energy-age" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Unit age
                  </label>
                  <select
                    id="freezer-energy-age"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="new">New (0–5 years)</option>
                    <option value="5-10">5–10 years (+15%)</option>
                    <option value="10-15">10–15 years (+30%)</option>
                    <option value="15+">15+ years (+50%)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="freezer-energy-location" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Installation location
                  </label>
                  <select
                    id="freezer-energy-location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="indoor">Indoor (kitchen / pantry)</option>
                    <option value="basement">Basement (-5%)</option>
                    <option value="garage">Garage (+20%)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Garage installs need a garage-rated unit.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Estimated annual cost
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {`$${result?.annualCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">/ yr</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result ? `${result.kwhPerYear} kWh per year at the EIA US average rate of 13¢/kWh.` : "Enter freezer details to see annual cost."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Monthly</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{`$${result?.monthlyCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "—"}`}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">10-year cost</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{`$${result?.tenYearCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Drop the bill"
          title="Upright freezers built for efficiency"
          description="Modern uprights run 300–400 kWh/yr — half what a 15-year-old unit pulls."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                DOE numbers. <em className="italic text-[#ff9951]">Real</em> rates.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Baseline kWh from DOE EnergyGuide testing; rate uses the EIA US residential average of 13¢/kWh.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Baseline kWh by type", d: "Uprights pull about 25 kWh per cu ft per year. Chest freezers — 20 kWh per cu ft — run roughly 20% leaner because cold air doesn't fall out." },
                { n: "02", t: "Age multiplier", d: "+15% for 5–10 yr units, +30% for 10–15 yr, +50% for 15+ yr. Compressor and gasket degradation is real." },
                { n: "03", t: "Location adjustment", d: "Garage +20% (hot summers), basement -5% (cooler ambient). Indoor is the baseline." },
                { n: "04", t: "Cost = kWh × rate", d: "Annual cost = annual kWh × $0.13 per kWh. Monthly = annual ÷ 12. Ten-year = annual × 10." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges — the efficiency leaders"
          description="The most efficient refrigerator configuration by design — often beats French door by 100+ kWh/yr."
          collectionType="top-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>
      {/* Server-rendered supporting data belongs inside the shell, before its footer. */}
      {children}
    </ToolPageShell>
  )
}
